import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { useEffect, useRef } from "react";
import { useRouteLoaderData, useRevalidator } from "react-router-dom";

import { App } from "@/App";
import { ROOT_ROUTE_ID } from "@/router";
import { supabase } from "@/supabaseClient";

const AUTH_APPEARANCE = {
  theme: ThemeSupa,
  className: {
    container: "py-2 w-full max-w-[400px]",
    input: "w-full",
    button: "w-full bg-[--colors-brand] text-white hover:bg-[--colors-brand-dark]",
  },
};

const AUTH_LOCALIZATION = {
  variables: {
    sign_up: {
      email_label: "El. paštas",
      password_label: "Slaptažodis",
      email_input_placeholder: "vardas@gmail.com",
      password_input_placeholder: "Slaptažodis",
      button_label: "Registruotis",
      loading_button_label: "Registruojama...",
      link_text: "Neturite paskyros? Registruokitės",
      confirmation_text:
        "Patvirtinkite savo el. pašto adresą, kad užbaigtumėte registraciją",
    },
    sign_in: {
      email_label: "El. paštas",
      email_input_placeholder: "vardas@gmail.com",
      password_label: "Slaptažodis",
      password_input_placeholder: "Slaptažodis",
      button_label: "Prisijungti",
      loading_button_label: "Prisijungiama...",
      link_text: "Jau turite paskyrą? Prisijunkite",
    },
    forgotten_password: {
      email_label: "El. paštas",
      email_input_placeholder: "vardas@gmail.com",
      button_label: "Atstatyti slaptažodį",
      loading_button_label: "Siunčiamos instrukcijos...",
      link_text: "Pamiršote slaptažodį?",
      confirmation_text:
        "Patikrinkite savo el. paštą, kad atstatytumėte slaptažodį",
    },
  },
};

interface RootLoaderData {
  tenant: unknown;
  user: unknown;
}

/**
 * Root route element: show Auth when there is no session (loader returned null
 * tenant/user), otherwise show App. Revalidates the root loader on auth state
 * change so after login the loader runs again with the client already holding
 * the session (no race, no timeouts).
 */
export function Root(): React.JSX.Element {
  const data = useRouteLoaderData(ROOT_ROUTE_ID) as RootLoaderData | undefined;
  const revalidator = useRevalidator();
  const revalidateRef = useRef(revalidator.revalidate);
  revalidateRef.current = revalidator.revalidate;

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      revalidateRef.current();
    });
    return () => subscription.unsubscribe();
  }, []);

  const hasSession = data && data.tenant != null;

  if (hasSession) {
    return <App />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Auth
        supabaseClient={supabase}
        providers={[]}
        appearance={AUTH_APPEARANCE}
        localization={AUTH_LOCALIZATION}
      />
    </div>
  );
}
